import React, { useRef, useEffect } from "react";
import { useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { Vector3 } from "three";

// Position d'origine configurable
export const cameraOriginalPosition = new Vector3(1, 0.375, -0.25);

/** Zoom molette / pinch — désactiver en remettant à false. */
export const ORBIT_ZOOM_ENABLED = false;

// Outils FOV (repris de ton code)
function degreesToRadians(degrees) {
  return degrees * (Math.PI / 180);
}
function getVerticalFovRad(camera) {
  return degreesToRadians(camera.fov);
}
function getHorizontalFovRad(camera) {
  const aspect = camera.aspect;
  const fov = getVerticalFovRad(camera);
  const dist = 1 / Math.tan(fov * 0.5);
  const halfFovH = Math.atan(aspect / dist);
  return halfFovH * 2;
}
function getNecessaryDistance(objectDimensionSize, dimensionFov, objectDepthFrom0) {
  const halfSize = 0.5 * objectDimensionSize;
  const halfFov = dimensionFov * 0.5;
  const baseNecessaryDistance = halfSize / Math.tan(halfFov);
  return baseNecessaryDistance + objectDepthFrom0;
}

/** Facteur de cadrage : plus élevé = caméra plus loin = modèle perçu plus petit. */
const CAMERA_FRAMING_FACTOR = 0.55 * 1.1;

/**
 * Profondeur cache (mm) utilisée pour le zoom — décorrélée de la profondeur saisie.
 * Largeur / hauteur pilotent toujours le cadrage ; la profondeur reste figée à 480 mm.
 */
const CAMERA_REFERENCE_DEPTH_MM = 480;

/**
 * Calcule la distance nécessaire pour cadrer le modèle (largeur + hauteur uniquement).
 */
function computeIdealDistance(camera, size) {
  const depthOffset = CAMERA_REFERENCE_DEPTH_MM * 0.5;

  const newDistanceVert = getNecessaryDistance(
    size.z * 0.1,
    getVerticalFovRad(camera),
    depthOffset,
  );
  const newDistanceHoriz = getNecessaryDistance(
    size.y * 0.1,
    getHorizontalFovRad(camera),
    depthOffset,
  );
  return Math.max(newDistanceVert, newDistanceHoriz) * CAMERA_FRAMING_FACTOR;
}

/**
 * Réinitialise la caméra pour cadrer le modèle (position + angle)
 * Utilisé uniquement au premier chargement
 */
function resetCamera(camera, orbitControls, size, targetCenter = null) {
  const newDistance = computeIdealDistance(camera, size);

  orbitControls.minDistance = newDistance / 3;
  orbitControls.maxDistance = newDistance * 2;

  const center = targetCenter || { x: 0, y: 0, z: 0 };

  camera.position.set(
    center.x + newDistance * 0.15,
    center.y + newDistance * 0.25,
    center.z + newDistance
  );
  orbitControls.target.set(center.x, center.y, center.z);
  camera.lookAt(center.x, center.y, center.z);
  orbitControls.update();
}

/**
 * Ajuste la distance de la caméra pour cadrer le modèle
 * SANS changer l'angle de vue (azimut/élévation préservés)
 */
function adjustCameraDistance(camera, orbitControls, size, targetCenter = null) {
  const newDistance = computeIdealDistance(camera, size);

  orbitControls.minDistance = newDistance / 3;
  orbitControls.maxDistance = newDistance * 2;

  const center = targetCenter || { x: 0, y: 0, z: 0 };

  const direction = new Vector3()
    .subVectors(camera.position, orbitControls.target)
    .normalize();

  camera.position.copy(
    new Vector3(center.x, center.y, center.z).add(direction.multiplyScalar(newDistance))
  );
  orbitControls.target.set(center.x, center.y, center.z);
  orbitControls.update();
}

function getCameraAzimuth(camera, target) {
  const offset = new Vector3().subVectors(camera.position, target);
  return Math.atan2(offset.z, offset.x);
}

function normalizeAngleDelta(delta) {
  let normalized = delta;
  while (normalized > Math.PI) normalized -= Math.PI * 2;
  while (normalized < -Math.PI) normalized += Math.PI * 2;
  return normalized;
}

/**
 * Camera3DControls - Contrôle de la caméra avec centrage automatique sur la bounding box
 */
const Camera3DControls = ({ size, autoRotate, shouldResetCamera = false, targetCenter = null }) => {
  const { camera } = useThree();
  const orbitControls = useRef();
  const swingCenterRef = useRef(0);
  // Sens du va-et-vient : survit aux pauses (chargement, hide3D) pour reprendre
  // dans le même sens au lieu de repartir systématiquement vers la droite (+1).
  const autoRotateDirectionRef = useRef(1);
  const prevShouldReset = useRef(false);
  const hasFullReset = useRef(false);

  const center = targetCenter || { x: 0, y: 0, z: 0 };

  const captureSwingCenter = () => {
    const controls = orbitControls.current;
    if (!controls) return;
    swingCenterRef.current = getCameraAzimuth(camera, controls.target);
  };

  useEffect(() => {
    if (!orbitControls.current) return;

    const isNewResetRequest = shouldResetCamera && !prevShouldReset.current;
    prevShouldReset.current = shouldResetCamera;

    if (isNewResetRequest) {
      resetCamera(camera, orbitControls.current, size, targetCenter);
      captureSwingCenter();
      if (targetCenter) hasFullReset.current = true;
    }
  }, [shouldResetCamera, camera, size, targetCenter]);

  useEffect(() => {
    if (!orbitControls.current) return;

    if (!hasFullReset.current) {
      if (targetCenter) {
        resetCamera(camera, orbitControls.current, size, targetCenter);
        captureSwingCenter();
        hasFullReset.current = true;
      }
    } else {
      adjustCameraDistance(camera, orbitControls.current, size, targetCenter);
    }
  }, [size.y, size.z, targetCenter?.x, targetCenter?.y, targetCenter?.z, camera]);

  // Va-et-vient : patch controls.update une fois que la ref OrbitControls est prête
  // (l'ancien useEffect sortait immédiatement car orbitControls.current === null).
  useEffect(() => {
    let cancelled = false;
    let restoreUpdate = null;

    const attachSwingPatch = () => {
      if (cancelled) return;

      const controls = orbitControls.current;
      if (!controls?.update) {
        requestAnimationFrame(attachSwingPatch);
        return;
      }

      const swingHalfRad = Math.PI / 3;

      const updateAutoRotate = () => {
        if (!controls.autoRotate) return;

        const azimuth = getCameraAzimuth(camera, controls.target);
        const delta = normalizeAngleDelta(azimuth - swingCenterRef.current);

        if (delta >= swingHalfRad) autoRotateDirectionRef.current = -1;
        else if (delta <= -swingHalfRad) autoRotateDirectionRef.current = 1;

        controls.autoRotateSpeed =
          Math.abs(controls.autoRotateSpeed || 1) * autoRotateDirectionRef.current;
      };

      const originalUpdate = controls.update.bind(controls);
      controls.update = () => {
        updateAutoRotate();
        originalUpdate();
      };

      restoreUpdate = () => {
        controls.update = originalUpdate;
      };
    };

    attachSwingPatch();

    return () => {
      cancelled = true;
      if (restoreUpdate) restoreUpdate();
    };
  }, [camera]);

  // OrbitControls remet autoRotateSpeed à la prop (toujours +1) quand autoRotate
  // repasse à true. Le sens est déjà dans autoRotateDirectionRef (mis à jour à
  // chaque frame pendant la rotation) — on le réapplique sans recréer le patch.
  useEffect(() => {
    if (!autoRotate) return;
    const raf = requestAnimationFrame(() => {
      const controls = orbitControls.current;
      if (!controls?.autoRotate) return;
      controls.autoRotateSpeed =
        Math.abs(controls.autoRotateSpeed || 1) * autoRotateDirectionRef.current;
    });
    return () => cancelAnimationFrame(raf);
  }, [autoRotate]);

  return (
    <OrbitControls
      ref={orbitControls}
      enableZoom={ORBIT_ZOOM_ENABLED}
      enablePan={false}
      enableRotate={autoRotate}
      autoRotate={autoRotate}
      autoRotateSpeed={1}
      target={[center.x, center.y, center.z]}
    />
  );
};

export default Camera3DControls;
